import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

const buttonVariants = cva(
  // Base styles
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-samsung-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-brand-samsung-500 text-white hover:bg-brand-samsung-600 active:bg-brand-samsung-700',
        destructive:
          'bg-accent-danger text-white hover:bg-red-600 dark:bg-red-700 dark:hover:bg-red-600',
        outline:
          'border border-[var(--surface-border)] bg-transparent text-[var(--text-primary)] hover:bg-[var(--surface-elevated)]',
        secondary:
          'bg-[var(--surface-elevated)] text-[var(--text-primary)] hover:bg-[var(--surface-border)]',
        ghost:
          'text-[var(--text-primary)] hover:bg-[var(--surface-elevated)]',
        link:
          'text-brand-samsung-500 underline-offset-4 hover:underline p-0 h-auto',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-7 rounded px-3 text-xs',
        lg: 'h-11 rounded-lg px-8 text-base',
        icon: 'h-9 w-9',
      },
      // V2 NEW (BULLE V2.2 T5) — status sémantique override
      status: {
        none: '',
        success: 'bg-status-success text-white hover:bg-emerald-600 active:bg-emerald-700',
        warning: 'bg-status-warning text-white hover:bg-amber-600 active:bg-amber-700',
        danger: 'bg-status-danger text-white hover:bg-red-600 active:bg-red-700',
      },
      // V2 NEW — fullWidth utility
      fullWidth: {
        true: 'w-full',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      status: 'none',
      fullWidth: false,
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /** V2 (BULLE V2.2 T5) — Loading state : spinner inline + disabled implicite. */
  loading?: boolean;
  /** V2 (BULLE V2.2 T5) — Icon avant ou après le label. */
  icon?: React.ReactNode;
  /** V2 (BULLE V2.2 T5) — Position de l'icône (default 'left'). */
  iconPosition?: 'left' | 'right';
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      status,
      fullWidth,
      asChild = false,
      loading = false,
      icon,
      iconPosition = 'left',
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    const isDisabled = disabled || loading;

    // En mode asChild (Radix Slot), on n'injecte pas d'icône/spinner pour rester
    // un wrapper transparent (comportement V1 préservé).
    if (asChild) {
      return (
        <Comp
          className={cn(buttonVariants({ variant, size, status, fullWidth, className }))}
          ref={ref}
          {...props}
        >
          {children}
        </Comp>
      );
    }

    return (
      <button
        className={cn(buttonVariants({ variant, size, status, fullWidth, className }))}
        ref={ref}
        disabled={isDisabled}
        aria-busy={loading || undefined}
        {...props}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
        {!loading && icon && iconPosition === 'left' && icon}
        {children}
        {!loading && icon && iconPosition === 'right' && icon}
      </button>
    );
  }
);
Button.displayName = 'Button';

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
